import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, all } from 'redux-saga/effects';
import { selectors, withOffset } from '../../../Stream';
import { STREAM__WITH_OFFSET__FETCH } from '../../../Stream/withOffset/actions';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import type { FetchAction } from '../../../Stream/base/types';
import { normalizeDeviations } from '../../../redux/normalizr/helpers';
import { FetchType } from '../../../Stream/base/types';

const ORIGINALS_COMPLETED_ORDERS_STREAM_PREFIX = 'originals_completed_orders';

export function getOriginalsCompletedOrdersStreamId(deviationId: number): string {
  return `${ORIGINALS_COMPLETED_ORDERS_STREAM_PREFIX}_${deviationId}`;
}

function* handleOriginalsCompletedOrdersFetch(action: FetchAction): SagaIterator {
  const { streamId } = action;

  // Extract deviation ID from stream ID
  if (!streamId.startsWith(ORIGINALS_COMPLETED_ORDERS_STREAM_PREFIX)) {
    return;
  }

  const streamParams: { deviationId: number } | undefined = yield select(
    selectors.getStreamParams,
    streamId
  );

  if (!streamParams?.deviationId) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Missing deviationId in stream params',
      })
    );
    return;
  }

  const {
    offset,
    limit,
  }: {
    offset: ReturnType<typeof withOffset.selectors.getNextOffset>;
    limit: ReturnType<typeof selectors.getItemsPerFetch>;
  } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
  }));

  const response: {
    deviations?: any[];
    has_more?: boolean;
    next_offset?: number;
  } | false = yield call(requestPuppy, {
    method: 'get',
    url: '/originals/completed_orders',
    params: {
      deviationid: streamParams.deviationId,
      offset: offset ?? 0,
      limit,
    },
  });

  if (!response || isApiError(response)) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch completed orders',
      })
    );
    return;
  }

  const { deviations, has_more: hasMore, next_offset: nextOffset } = response;
  const normalized = deviations && normalizeDeviations(deviations);

  yield put(
    withOffset.actions.fetchSuccess({
      streamId,
      hasMore: !!hasMore,
      nextOffset,
      items: normalized?.result ?? [],
      entities: normalized?.entities ?? {},
    })
  );
}

export default function* originalsDetailsSaga() {
  yield all([
    takeEvery(
      (action: any) =>
        action.type === STREAM__WITH_OFFSET__FETCH &&
        action.streamId?.startsWith(ORIGINALS_COMPLETED_ORDERS_STREAM_PREFIX),
      handleOriginalsCompletedOrdersFetch
    ),
  ]);
}
